import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, RefreshCcw, UserCheck, MessageSquare, Globe, Calendar, Save, UserPlus, Trash2, Mail, Check, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { SpecialistCard, AnySpecialistCard, AvailabilityPill } from '../components/SpecialistCard';
import { fmtDate, fmtTime } from '../lib/format';
import { toast } from 'sonner';

const COMMON_LANGUAGES = ['English', 'French', 'Spanish', 'Vietnamese', 'Mandarin', 'Portuguese'];

export default function MemberSpecialistPage() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [pref, setPref] = useState({ preferred_specialist_id: null, allow_any: true, preferred_language: '' });
  const [dirty, setDirty] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeForm, setChangeForm] = useState({ requested_specialist_id: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [offers, setOffers] = useState([]);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [addingId, setAddingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [mine, list, prefData, offerList] = await Promise.all([
        api.myAssignedSpecialist(),
        api.myBookingSpecialists(),
        api.getSpecialistPreference(),
        api.myOffers('pending').catch(() => []),
      ]);
      setMe(mine);
      setSpecialists(list.specialists || []);
      setPref({
        preferred_specialist_id: prefData.preferred_specialist_id || null,
        allow_any: prefData.allow_any !== false,
        preferred_language: prefData.preferred_language || '',
      });
      setOffers(offerList || []);
    } catch {
      toast.error('Could not load your specialist info');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updatePref = (patch) => {
    setPref((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const savePref = async () => {
    try {
      await api.updateSpecialistPreference(pref);
      setDirty(false);
      toast.success('Preferences saved');
    } catch { toast.error('Could not save preferences'); }
  };

  const submitChange = async () => {
    if (!changeForm.reason.trim()) { toast.error('Please tell us why'); return; }
    setSubmitting(true);
    try {
      const out = await api.requestSpecialistChange(changeForm);
      if (out.status === 'approved') toast.success('Specialist changed!');
      else toast.success('Request submitted to admin');
      setChangeOpen(false);
      setChangeForm({ requested_specialist_id: '', reason: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit request');
    } finally { setSubmitting(false); }
  };

  const directAdd = async (specialist_user_id) => {
    setAddingId(specialist_user_id);
    try {
      await api.memberAddSpecialist(specialist_user_id);
      toast.success('Tax Specialist added — you can now share documents with them.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add specialist');
    } finally { setAddingId(null); }
  };

  const submitRemove = async () => {
    if (!removeReason.trim()) { toast.error('Please tell us why'); return; }
    setSubmitting(true);
    try {
      await api.memberRequestRemoveSpecialist(removeReason);
      toast.success('Removal request sent — an admin will confirm shortly.');
      setRemoveOpen(false); setRemoveReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send removal request');
    } finally { setSubmitting(false); }
  };

  const decideOffer = async (id, decision) => {
    try {
      const out = await api.decideOffer(id, decision);
      if (decision === 'accept' && out.applied_immediately) {
        toast.success('Specialist accepted and assigned!');
      } else if (decision === 'accept') {
        toast.success('Offer accepted — admin approval pending');
      } else {
        toast.success('Offer declined');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not process offer');
    }
  };

  const current = me?.specialist || null;

  return (
    <>
      <Header />
      <section className="py-12 bg-[#fbf7ee] border-b border-neutral-100">
        <div className="max-w-[960px] mx-auto px-6">
          <Link to="/account" data-testid="back-to-account"
                className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#14110f] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to account
          </Link>
          <p className="section-eyebrow mb-1">Your Tax Specialist</p>
          <h1 className="text-[28px] md:text-[32px] font-extrabold text-[#14110f] leading-tight">
            Meet, manage and request a change of specialist
          </h1>
          <p className="text-[13.5px] text-neutral-600 mt-1 max-w-xl">
            We&rsquo;ll consider your preferences every time you book — and you can always ask for a different specialist.
          </p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[960px] mx-auto px-6 space-y-10">

          {/* Pending offers from specialists */}
          {offers.length > 0 && (
            <div data-testid="pending-offers-card"
                 className="rounded-xl border border-[#f5b300] bg-gradient-to-br from-[#fff8e3] to-white p-6 md:p-7">
              <h2 className="text-[18px] font-bold text-[#14110f] flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-[#c89200]" />
                You have {offers.length} pending specialist offer{offers.length === 1 ? '' : 's'}
              </h2>
              <p className="text-[12.5px] text-neutral-600 mb-4">
                A Tax Specialist has offered to work with you. Accept or decline below.
              </p>
              <ul className="space-y-3">
                {offers.map((o) => (
                  <li key={o.id} data-testid={`offer-${o.id}`}
                      className="rounded-md bg-white border border-neutral-200 px-4 py-3">
                    <p className="text-[14px] font-semibold text-[#14110f]">{o.specialist_name}</p>
                    {o.message && (
                      <p className="mt-1 text-[12.5px] text-neutral-600 italic">&ldquo;{o.message}&rdquo;</p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <button data-testid={`offer-accept-${o.id}`}
                              onClick={() => decideOffer(o.id, 'accept')}
                              className="inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-3 py-1.5 rounded-md text-[12.5px]">
                        <Check className="w-3.5 h-3.5" />Accept
                      </button>
                      <button data-testid={`offer-decline-${o.id}`}
                              onClick={() => decideOffer(o.id, 'decline')}
                              className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-rose-400 text-neutral-700 hover:text-rose-600 font-semibold px-3 py-1.5 rounded-md text-[12.5px]">
                        <X className="w-3.5 h-3.5" />Decline
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Currently assigned */}
          <div data-testid="my-specialist-widget"
               className="rounded-xl border border-neutral-200 bg-white p-6 md:p-7">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-[18px] font-bold text-[#14110f] flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-[#c89200]" />My current specialist
              </h2>
              {current && (
                <div className="flex items-center gap-3">
                  <button data-testid="open-change-btn" onClick={() => setChangeOpen((v) => !v)}
                          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#c89200] hover:underline">
                    <RefreshCcw className="w-3.5 h-3.5" />Request a different specialist
                  </button>
                  <button data-testid="open-remove-btn" onClick={() => setRemoveOpen((v) => !v)}
                          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-600 hover:underline">
                    <Trash2 className="w-3.5 h-3.5" />Remove
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="h-[120px] rounded-md bg-neutral-100 animate-pulse" />
            ) : current ? (
              <div className="flex flex-col md:flex-row gap-5 items-start">
                <div className="md:w-[320px] shrink-0">
                  <SpecialistCard specialist={current} selected onSelect={() => {}}
                                  testId="my-specialist-card" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="grid grid-cols-2 gap-3 text-[13px]">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Status</p>
                      <p className="mt-1"><AvailabilityPill status={current.availability} /></p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Active clients</p>
                      <p className="mt-1 font-bold text-[#14110f]">{current.workload?.active_clients ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Languages</p>
                      <p className="mt-1 text-neutral-700">{(current.languages || []).join(', ') || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Timezone</p>
                      <p className="mt-1 text-neutral-700">{current.timezone || '—'}</p>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-neutral-100 pt-4">
                    <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Upcoming appointments</p>
                    {me.upcoming_appointments.length === 0 ? (
                      <p className="text-[13px] text-neutral-500 mt-2">
                        No upcoming appointments. <Link to="/book" className="text-[#c89200] font-semibold hover:underline">Book one</Link>.
                      </p>
                    ) : (
                      <ul className="mt-2 space-y-1.5">
                        {me.upcoming_appointments.map((a) => (
                          <li key={a.id} className="flex items-center gap-2 text-[13px] text-neutral-700">
                            <Calendar className="w-3.5 h-3.5 text-[#c89200]" />
                            <span className="font-semibold">{a.appointment_type_name}</span>
                            <span className="text-neutral-400">·</span>
                            <span>{fmtDate(a.start)} · {fmtTime(a.start)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div data-testid="add-specialist-picker" className="space-y-4">
                <div className="text-center">
                  <UserCheck className="w-10 h-10 mx-auto text-neutral-300 mb-2" />
                  <p className="text-[14px] text-[#14110f] font-semibold">
                    {offers.length > 0 ? 'Or pick someone else' : 'Pick your Tax Specialist'}
                  </p>
                  <p className="text-[12.5px] text-neutral-500 mt-1">
                    Adding a specialist immediately gives them access to your tax documents.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {specialists.map((s) => (
                    <div key={s.id} className="relative">
                      <SpecialistCard
                        specialist={s}
                        selected={false}
                        onSelect={() => directAdd(s.id)}
                        testId={`add-spec-${s.id}`}
                        compact
                      />
                      <button data-testid={`add-spec-btn-${s.id}`}
                              disabled={addingId === s.id}
                              onClick={() => directAdd(s.id)}
                              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-3 py-2 rounded-md text-[12.5px]">
                        <UserPlus className="w-3.5 h-3.5" />
                        {addingId === s.id ? 'Adding…' : `Choose ${(s.name || '').split(' ')[0]}`}
                      </button>
                    </div>
                  ))}
                </div>
                {specialists.length === 0 && (
                  <p className="text-center text-[12.5px] text-neutral-500 italic">
                    No specialists available right now.
                  </p>
                )}
              </div>
            )}

            {removeOpen && (
              <div data-testid="remove-request-form"
                   className="mt-6 border-t border-neutral-100 pt-5">
                <h3 className="text-[14.5px] font-bold text-[#14110f] mb-3 flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-rose-600" />Remove your Tax Specialist
                </h3>
                <p className="text-[12.5px] text-neutral-500 mb-3">
                  Removing a specialist requires admin approval. Once approved, they
                  immediately lose access to your tax documents.
                </p>
                <textarea
                  data-testid="remove-reason"
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  rows={3}
                  placeholder="Tell us why you'd like to remove them…"
                  className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] text-[#14110f] placeholder:text-neutral-400 focus:border-rose-400 outline-none bg-white"
                />
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid="remove-submit" onClick={submitRemove} disabled={submitting}
                          className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-md text-[13px]">
                    <Trash2 className="w-3.5 h-3.5" />{submitting ? 'Sending…' : 'Send removal request'}
                  </button>
                  <button onClick={() => { setRemoveOpen(false); setRemoveReason(''); }}
                          className="text-[12.5px] text-neutral-500 hover:text-[#14110f]">Cancel</button>
                </div>
              </div>
            )}

            {changeOpen && (
              <div data-testid="change-request-form"
                   className="mt-6 border-t border-neutral-100 pt-5">
                <h3 className="text-[14.5px] font-bold text-[#14110f] mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#c89200]" />Why would you like a different specialist?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Specialist you&rsquo;d prefer (optional)</label>
                    <select
                      data-testid="change-request-target"
                      value={changeForm.requested_specialist_id}
                      onChange={(e) => setChangeForm({ ...changeForm, requested_specialist_id: e.target.value })}
                      className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none bg-white">
                      <option value="">No preference</option>
                      {specialists.filter((s) => s.id !== current?.id).map((s) => (
                        <option key={s.id} value={s.id}>{s.name} — {s.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Tell us why</label>
                    <textarea
                      data-testid="change-request-reason"
                      value={changeForm.reason}
                      onChange={(e) => setChangeForm({ ...changeForm, reason: e.target.value })}
                      rows={3}
                      placeholder="A short note helps our team route you faster…"
                      className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button data-testid="change-request-submit" onClick={submitChange} disabled={submitting}
                          className="inline-flex items-center gap-1.5 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px]">
                    <Send className="w-3.5 h-3.5" />{submitting ? 'Sending…' : 'Submit request'}
                  </button>
                  <button onClick={() => setChangeOpen(false)} className="text-[12.5px] text-neutral-500 hover:text-[#14110f]">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6 md:p-7">
            <h2 className="text-[18px] font-bold text-[#14110f]">Booking preferences</h2>
            <p className="text-[13px] text-neutral-500 mt-1">
              These guide auto-assignment when you select <em>Any Available Specialist</em> at booking.
            </p>

            <div className="mt-5 space-y-5">
              <div>
                <p className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Preferred specialist</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <AnySpecialistCard
                    selected={!pref.preferred_specialist_id}
                    onSelect={() => updatePref({ preferred_specialist_id: null })}
                    testId="pref-any"
                  />
                  {specialists.map((s) => (
                    <SpecialistCard
                      key={s.id}
                      specialist={s}
                      selected={pref.preferred_specialist_id === s.id}
                      onSelect={() => updatePref({ preferred_specialist_id: s.id })}
                      testId={`pref-spec-${s.id}`}
                      compact
                    />
                  ))}
                </div>
              </div>

              <div className="border-t border-neutral-100 pt-5">
                <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />Preferred language
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {['', ...COMMON_LANGUAGES].map((lang) => (
                    <button
                      key={lang || 'any'}
                      data-testid={`pref-lang-${lang || 'any'}`}
                      onClick={() => updatePref({ preferred_language: lang })}
                      className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition ${
                        (pref.preferred_language || '') === lang
                          ? 'bg-[#14110f] text-white'
                          : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#f5b300]'
                      }`}>
                      {lang || 'No preference'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-neutral-100 pt-5 flex items-center gap-3">
                <input
                  id="pref-allow-any"
                  data-testid="pref-allow-any"
                  type="checkbox"
                  checked={pref.allow_any}
                  onChange={(e) => updatePref({ allow_any: e.target.checked })}
                  className="w-4 h-4 accent-[#f5b300]"
                />
                <label htmlFor="pref-allow-any" className="text-[13.5px] text-[#14110f] cursor-pointer">
                  Allow auto-assignment to other qualified specialists when my preferred one is unavailable
                </label>
              </div>

              <div className="border-t border-neutral-100 pt-5 flex items-center justify-end gap-3">
                <button data-testid="save-pref-btn" onClick={savePref} disabled={!dirty}
                        className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:bg-neutral-200 disabled:text-neutral-500 disabled:cursor-not-allowed text-[#14110f] font-semibold px-5 py-2.5 rounded-md transition">
                  <Save className="w-4 h-4" />{dirty ? 'Save preferences' : 'Saved'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
